import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  InfoSection,
  PropertySectionHeader as SectionHeader,
  PropertySectionTitle as SectionTitle,
  SectionIcon,
  SectionCount,
  SectionContent,
  ExpandableList,
  ExpandableItem,
  ExpandableHeader,
  ExpandableInfo,
  ExpandableTitle,
  ExpandableSubtitle,
  ExpandableContent,
  ExpandableDetails,
  RestrictionStatus,
  PropertyDataRow as DataRow,
  DataValue,
} from '../../../molecules/PropertyPanelStyles'
import { DataLabelWithTooltip } from '../../../molecules/DataLabelWithTooltip'
import { Icon } from '../../../atoms/Icon'
import { ExpandArrow } from '../../../atoms/ExpandArrow'
import { translateRdppfZoneName } from '../../../../utils/rdppfTranslations'
import type { RdppfWithType, LandRdppf } from '@popety-io/types'

// Styled component for external links in extra info
const ExtraInfoLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  transition: color ${({ theme }) => theme.transitions.fast};
  word-break: break-all;

  &:hover {
    color: ${({ theme }) => theme.colors.primaryHover};
    text-decoration: underline;
  }
`

// Utility: Check if a value is a URL
const isUrl = (value: string): boolean => {
  return (
    typeof value === 'string' &&
    (value.startsWith('http://') || value.startsWith('https://'))
  )
}

// Utility: Extract displayable string from an object
const extractStringFromObject = (
  obj: Record<string, unknown>
): string | null => {
  for (const [key, val] of Object.entries(obj)) {
    if (key.startsWith('@')) continue
    if (
      typeof val === 'string' &&
      val.trim() !== '' &&
      val.trim().toLowerCase() !== 'null'
    ) {
      return val
    }
  }
  return null
}

// Utility: Try to parse and extract value from JSON string
const parseJsonString = (value: string): string | null => {
  try {
    const parsed = JSON.parse(value)
    if (Array.isArray(parsed) && parsed.length > 0) {
      const first = parsed[0]
      if (typeof first === 'object' && first !== null) {
        return extractStringFromObject(first)
      }
      if (typeof first === 'string') return first
    }
    if (typeof parsed === 'object' && parsed !== null) {
      return extractStringFromObject(parsed)
    }
    return null
  } catch {
    return null
  }
}

// Utility: Check if string looks like JSON
const isJsonString = (value: string): boolean => {
  const trimmed = value.trim()
  return (
    (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
    (trimmed.startsWith('{') && trimmed.endsWith('}'))
  )
}

// Utility: Check if a value should be displayed
const isValidValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false
  if (Array.isArray(value)) return false
  if (typeof value === 'object') {
    return extractStringFromObject(value as Record<string, unknown>) !== null
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed === '' || trimmed.toLowerCase() === 'null') return false
    if (isJsonString(trimmed)) {
      return parseJsonString(trimmed) !== null
    }
    return true
  }
  return typeof value === 'number' || typeof value === 'boolean'
}

// Utility: Convert value to displayable string
const toDisplayValue = (
  value: unknown,
  tFn?: (key: string) => string
): string => {
  if (typeof value === 'string') {
    if (isJsonString(value)) {
      const extracted = parseJsonString(value)
      if (extracted) return extracted
    }
    return value
  }
  if (typeof value === 'number') return String(value)
  if (typeof value === 'boolean') {
    if (tFn) return value ? tFn('common.yes') : tFn('common.no')
    return value ? 'Yes' : 'No'
  }
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    return extractStringFromObject(value as Record<string, unknown>) || ''
  }
  return ''
}

// Utility: Format URL for display (domain + truncated path)
const formatUrlForDisplay = (url: string): string => {
  try {
    const parsed = new URL(url)
    const path =
      parsed.pathname.length > 20
        ? parsed.pathname.slice(0, 20) + '...'
        : parsed.pathname
    return `${parsed.hostname}${path}`
  } catch {
    return url.slice(0, 40) + (url.length > 40 ? '...' : '')
  }
}

interface RestrictionsSectionProps {
  rdppfs: LandRdppf[]
  landId?: string
  fetchFullRdppfData?: (landId: string) => Promise<RdppfWithType[]>
}

export const RestrictionsSection: React.FC<RestrictionsSectionProps> = ({
  rdppfs,
  landId,
  fetchFullRdppfData,
}) => {
  const { t } = useTranslation('explore')
  const { t: tCommon } = useTranslation('common')
  const { t: tRestrictions } = useTranslation('restrictions')
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())
  const [fullRdppfData, setFullRdppfData] = useState<RdppfWithType[]>([])

  // Fetch full RDPPF data with extra_information_json
  useEffect(() => {
    const fetchData = async () => {
      if (!landId || !fetchFullRdppfData) return
      try {
        const data = await fetchFullRdppfData(landId)
        setFullRdppfData(data)
      } catch (error) {
        console.error('Error fetching RDPPF data:', error)
      }
    }
    fetchData()
  }, [landId, fetchFullRdppfData])

  // Build display name with servitude type if available (for header display)
  const getDisplayName = (
    restriction: any,
    extraJson?: Record<string, unknown>
  ): string => {
    const baseName =
      restriction.name ||
      restriction.rdppf_type?.name ||
      t('propertyTooltip.restrictions.restriction')
    let displayName = translateRdppfZoneName(baseName, tRestrictions)
    const servitudeClass = extraJson?.['Classe de la servitude']
    if (typeof servitudeClass === 'string' && servitudeClass) {
      const servitudeType = servitudeClass.split('.')[0].trim()
      if (
        servitudeType &&
        servitudeType !== 'Null' &&
        !displayName.toLowerCase().includes(servitudeType.toLowerCase())
      ) {
        displayName = `${displayName} - ${servitudeType}`
      }
    }
    return displayName
  }

  // Render extra information dynamically from any restriction type
  const renderExtraInfo = (extraJson: Record<string, unknown> | undefined) => {
    if (!extraJson) return null

    const validEntries = Object.entries(extraJson).filter(([_, value]) =>
      isValidValue(value)
    )

    if (validEntries.length === 0) return null

    return validEntries.map(([key, value]) => {
      const displayValue = toDisplayValue(value, tCommon)
      return (
        <DataRow key={key}>
          <DataLabelWithTooltip label={key}>{key}</DataLabelWithTooltip>
          <DataValue>
            {isUrl(displayValue) ? (
              <ExtraInfoLink
                href={displayValue}
                target="_blank"
                rel="noopener noreferrer"
                title={displayValue}
              >
                {formatUrlForDisplay(displayValue)}
                <Icon name="external-link" size="small" />
              </ExtraInfoLink>
            ) : (
              displayValue
            )}
          </DataValue>
        </DataRow>
      )
    })
  }

  const toggleExpand = (index: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(index)) {
        newSet.delete(index)
      } else {
        newSet.add(index)
      }
      return newSet
    })
  }

  // Merge rdppfs from Elasticsearch with full data from API
  const restrictionsToDisplay = useMemo(() => {
    if (fullRdppfData.length > 0) {
      return fullRdppfData.map((r) => {
        const basicRdppf = rdppfs.find((br: any) => {
          const brTypeId = br.rdppf_type_id?.toString()
          const rTypeId = r.rdppf_type_id?.toString()
          return brTypeId === rTypeId
        })
        return {
          ...r,
          cover_area_perc: basicRdppf?.cover_area_perc,
          active: r.active !== false,
        }
      })
    }
    return rdppfs
  }, [fullRdppfData, rdppfs])

  if (!restrictionsToDisplay || restrictionsToDisplay.length === 0) {
    return null
  }

  return (
    <InfoSection>
      <SectionHeader>
        <SectionTitle>
          <SectionIcon>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
          </SectionIcon>
          {t('propertyTooltip.restrictions.title')}
        </SectionTitle>
        <SectionCount>{restrictionsToDisplay.length}</SectionCount>
      </SectionHeader>
      <SectionContent>
        <ExpandableList>
          {restrictionsToDisplay.map((restriction: any, index) => {
            const restrictionId = `${index}`
            const isExpanded = expandedItems.has(restrictionId)
            const isActive = restriction.active !== false
            const extraJson = restriction.extra_information_json as
              | Record<string, unknown>
              | undefined
            const displayName = getDisplayName(restriction, extraJson)

            return (
              <ExpandableItem key={restrictionId} $expanded={isExpanded}>
                <ExpandableHeader onClick={() => toggleExpand(restrictionId)}>
                  <ExpandableInfo>
                    <div>
                      <ExpandableTitle>{displayName}</ExpandableTitle>
                      <ExpandableSubtitle>
                        <RestrictionStatus $active={isActive}>
                          {isActive
                            ? t('propertyTooltip.restrictions.active')
                            : t('propertyTooltip.restrictions.inactive')}
                        </RestrictionStatus>
                        {restriction.cover_area_perc && (
                          <span>
                            {t('propertyTooltip.restrictions.coverage', {
                              percent: (
                                restriction.cover_area_perc * 100
                              ).toFixed(0),
                            })}
                          </span>
                        )}
                      </ExpandableSubtitle>
                    </div>
                  </ExpandableInfo>
                  <ExpandArrow $expanded={isExpanded} direction="down" />
                </ExpandableHeader>
                <ExpandableContent $expanded={isExpanded}>
                  <ExpandableDetails>
                    {restriction.rdppf_type?.name && (
                      <DataRow>
                        <DataLabelWithTooltip
                          label={t('propertyTooltip.restrictions.type')}
                          explanation={tCommon(
                            'propertyExplanations.restrictions.type'
                          )}
                        >
                          {t('propertyTooltip.restrictions.type')}
                        </DataLabelWithTooltip>
                        <DataValue>
                          {translateRdppfZoneName(
                            restriction.rdppf_type.name,
                            tRestrictions
                          )}
                        </DataValue>
                      </DataRow>
                    )}
                    {restriction.cover_area_perc !== undefined && (
                      <DataRow>
                        <DataLabelWithTooltip
                          label={t('propertyTooltip.restrictions.coverageArea')}
                          explanation={tCommon(
                            'propertyExplanations.restrictions.coverageArea'
                          )}
                        >
                          {t('propertyTooltip.restrictions.coverageArea')}
                        </DataLabelWithTooltip>
                        <DataValue>
                          {(restriction.cover_area_perc * 100).toFixed(1)}%
                        </DataValue>
                      </DataRow>
                    )}
                    <DataRow>
                      <DataLabelWithTooltip
                        label={t('propertyTooltip.restrictions.status')}
                        explanation={tCommon(
                          'propertyExplanations.restrictions.status'
                        )}
                      >
                        {t('propertyTooltip.restrictions.status')}
                      </DataLabelWithTooltip>
                      <DataValue>
                        {isActive ? t('common.active') : t('common.inactive')}
                      </DataValue>
                    </DataRow>

                    {/* Dynamic extra information (handles all restriction types) */}
                    {renderExtraInfo(extraJson)}

                    {restriction.rdppf_type?.new_construction_authorized !==
                      undefined && (
                      <DataRow>
                        <DataLabelWithTooltip
                          label={t(
                            'propertyTooltip.restrictions.newConstruction'
                          )}
                          explanation={tCommon(
                            'propertyExplanations.restrictions.newConstruction'
                          )}
                        >
                          {t('propertyTooltip.restrictions.newConstruction')}
                        </DataLabelWithTooltip>
                        <DataValue>
                          {restriction.rdppf_type.new_construction_authorized
                            ? t('common.yes')
                            : t('common.no')}
                        </DataValue>
                      </DataRow>
                    )}
                    {restriction.rdppf_type?.description && (
                      <DataRow>
                        <DataLabelWithTooltip
                          label={t('propertyTooltip.restrictions.description')}
                          explanation={tCommon(
                            'propertyExplanations.restrictions.description'
                          )}
                        >
                          {t('propertyTooltip.restrictions.description')}
                        </DataLabelWithTooltip>
                        <DataValue>
                          {restriction.rdppf_type.description}
                        </DataValue>
                      </DataRow>
                    )}
                  </ExpandableDetails>
                </ExpandableContent>
              </ExpandableItem>
            )
          })}
        </ExpandableList>
      </SectionContent>
    </InfoSection>
  )
}
